/**
 * Godkännande av ombyggnads- och ändringsåtgärder enligt 12 kap. 18 d §
 * jordabalken (Formulär R-07).
 *
 * Two pages. Page 1 is the landlord's byggbeskrivning with its bilagor;
 * page 2 is the överenskommelse the tenant(s) sign. Both pages repeat the
 * hyresvärd / hyresobjekt / hyresgäst panels under the same § 1–3.
 *
 * Page 2 restarts numbering (see `headerSection`) and reads § 1–5
 * continuously: the repeated § 1–3, then godkännande § 4 and underskrift § 5.
 * The legacy form's resumed "§ 6/§ 7" is deliberately not reproduced. A fixed
 * `number` instance input is still honoured for compositions stored with it.
 *
 * Legal text verbatim from `base-templates/18 d godkännande och 18e
 * meddelande.docx` (Haymanot-approved). DO NOT paraphrase or reorder.
 */
import { em, h, nodes, option, optionList, p, pageBreak, section, t, val } from '@hyranai/regta-document-ir';
import { partyGroupNodes, partyGroupSlots, shown, slotText, ticked } from '../common/index.js';
import { HYRESOBJEKT_SLOTS, LANDLORD_FIELDS, LEGAL_18, TENANT_CONTACT_FIELDS, TENANT_FIELDS, hyresobjektRows, } from '../meddelande_18e/blocks.js';
import { SIGNING_PLACE_DATE_SLOTS, answer, flag, legalSigningBlock, placeDateLine } from '../meddelande_18e/kit.js';
const COTENANT = ' (medhyresgäst)';
/**
 * One of the repeated header panels. On page 2 (instance input `part_ref`)
 * the panel gets its own ref; the first page-2 panel carries
 * `restart_numbering` so page 2 prints § 1–3 again.
 */
function headerSection(ref, title, content, input) {
    const partRef = slotText(input.part_ref);
    if (!partRef)
        return section(ref, title, content);
    return section(`${ref}-${partRef}`, title, content, input.restart_numbering === true ? { restartNumbering: true } : {});
}
const LANDLORD = { role: 'lessor', ordinal: 0, fields: LANDLORD_FIELDS };
export const hyresvard = {
    ...LEGAL_18,
    blockKey: 'godkannande_18d.hyresvard',
    version: 'v1',
    title: 'Uppgifter om hyresvärden',
    summary: 'The landlord’s name, id number and address.',
    slots: partyGroupSlots(LANDLORD),
    compile: (input, ctx) => [headerSection('hyresvard', 'Uppgifter om hyresvärden', partyGroupNodes(LANDLORD, input, ctx), input)],
};
export const hyresobjekt = {
    ...LEGAL_18,
    blockKey: 'godkannande_18d.hyresobjekt',
    version: 'v1',
    title: 'Uppgifter om hyresobjekt',
    summary: 'The apartment: number, rooms, area, address and fastighetsbeteckning.',
    slots: HYRESOBJEKT_SLOTS,
    compile: (input, ctx) => [headerSection('hyresobjekt', 'Uppgifter om hyresobjekt', hyresobjektRows(input, ctx), input)],
};
const COTENANT_FIELDS = [...TENANT_FIELDS, ...TENANT_CONTACT_FIELDS].map((f) => ({
    ...f,
    key: f.key.replace(/^tenant_/, 'cotenant_'),
    label: `${f.label}${COTENANT}`,
    required: false,
}));
/**
 * The tenant panel. The co-tenant's slots are declared (the editor fills
 * them, the signing panels name the person) but only the primary tenant
 * prints. Instance input `with_contact: false` drops e-post/telefon (page 2).
 */
export const hyresgast = {
    ...LEGAL_18,
    blockKey: 'godkannande_18d.hyresgast',
    version: 'v1',
    title: 'Uppgifter om hyresgästen',
    summary: 'The tenant’s name and id number, plus contact details on page 1.',
    slots: [
        ...partyGroupSlots({ role: 'lessee', ordinal: 0, fields: [...TENANT_FIELDS, ...TENANT_CONTACT_FIELDS] }),
        ...partyGroupSlots({ role: 'lessee', ordinal: 1, fields: COTENANT_FIELDS }),
    ],
    compile: (input, ctx) => {
        const fields = input.with_contact === false ? TENANT_FIELDS : [...TENANT_FIELDS, ...TENANT_CONTACT_FIELDS];
        return [
            headerSection('hyresgast', 'Uppgifter om hyresgästen', partyGroupNodes({ role: 'lessee', ordinal: 0, fields }, input, ctx), input),
        ];
    },
};
export const byggbeskrivning = {
    ...LEGAL_18,
    blockKey: 'godkannande_18d.byggbeskrivning',
    version: 'v1',
    title: 'Byggbeskrivning',
    summary: 'The works the landlord will carry out in the common parts and in the apartment.',
    slots: [
        {
            key: 'works_common_areas',
            label: 'Åtgärder i fastighetens gemensamma delar',
            type: 'textarea',
            help: 'Specificera åtgärderna som ska genomföras i fastighetens gemensamma delar.',
        },
        {
            key: 'works_apartment',
            label: 'Åtgärder i lägenheten',
            type: 'textarea',
            help: 'Specificera åtgärderna som ska genomföras i lägenheten.',
        },
    ],
    compile: (input) => [
        section('byggbeskrivning', 'Byggbeskrivning', [
            p('I fastighetens gemensamma delar ska fastighetsägaren genomföra följande delar:'),
            answer(input, 'works_common_areas'),
            p('I lägenheten ska fastighetsägaren genomföra följande åtgärder:'),
            answer(input, 'works_apartment'),
        ]),
    ],
};
export const bilagor = {
    ...LEGAL_18,
    blockKey: 'godkannande_18d.bilagor',
    version: 'v1',
    title: 'Bilagor',
    summary: 'Which drawings and other enclosures accompany the byggbeskrivning.',
    slots: [
        { key: 'annex_byggritning', label: 'Byggritning bifogas', type: 'boolean' },
        { key: 'annex_planritning', label: 'Planritning bifogas', type: 'boolean' },
        { key: 'annex_other', label: 'Annan bilaga bifogas', type: 'boolean' },
        {
            key: 'annex_other_description',
            label: 'Beskrivning av annan bilaga',
            type: 'text',
            visibleWhen: { key: 'annex_other', equals: true },
        },
    ],
    compile: (input, ctx) => {
        const description = slotText(input.annex_other_description);
        const other = ctx.mode === 'blank' || description
            ? [val(description, { sourceKey: 'annex_other_description', blankWidth: 'long' })]
            : [t('Annan bilaga')];
        return [
            section('bilagor', 'Bilagor', [
                optionList([
                    option(ticked(ctx, flag(input, 'annex_byggritning')), 'Byggritning', { code: 'annex_byggritning', sourceKey: 'annex_byggritning' }),
                    option(ticked(ctx, flag(input, 'annex_planritning')), 'Planritning', { code: 'annex_planritning', sourceKey: 'annex_planritning' }),
                    option(ticked(ctx, flag(input, 'annex_other')), other, { code: 'annex_other', sourceKey: 'annex_other' }),
                ], { select: 'multi' }),
            ]),
        ];
    },
};
export const overenskommelseRubrik = {
    ...LEGAL_18,
    blockKey: 'godkannande_18d.overenskommelse-rubrik',
    version: 'v1',
    title: 'Överenskommelse om ändringsarbete i bostadslägenhet',
    summary: 'Page break and the heading of page 2, the agreement the tenant signs.',
    slots: [],
    compile: () => [
        pageBreak(),
        h(2, 'Överenskommelse om ändringsarbete i bostadslägenhet', { align: 'center', keepWithNext: true }),
    ],
};
export const godkannande = {
    ...LEGAL_18,
    blockKey: 'godkannande_18d.godkannande',
    version: 'v1',
    title: 'Hyresgästens godkännande',
    summary: 'The tenant approves the described works (common parts and/or apartment) and undertakes access and emptying.',
    slots: [
        { key: 'consent_common_areas', label: 'Hyresgästen godkänner åtgärder i fastighetens gemensamma delar', type: 'boolean' },
        { key: 'consent_apartment', label: 'Hyresgästen godkänner åtgärder i lägenheten', type: 'boolean' },
    ],
    compile: (input, ctx) => {
        const number = slotText(input.number);
        return [
            section('godkannande', 'Hyresgästens godkännande', [
                p('Hyresgästen godkänner att hyresvärden får utföra de ombyggnads- och ändringsåtgärder som specificeras i byggbeskrivningen ovan avseende:'),
                optionList([
                    option(ticked(ctx, flag(input, 'consent_common_areas')), 'Fastighetens gemensamma delar', { code: 'consent_common_areas', sourceKey: 'consent_common_areas' }),
                    option(ticked(ctx, flag(input, 'consent_apartment')), 'Lägenheten', { code: 'consent_apartment', sourceKey: 'consent_apartment' }),
                ], { select: 'multi' }),
                p('Hyresgästen förbinder sig att lämna hyresvärden tillträde till lägenheten under den tidsperiod och vid de tillfällen som krävs för arbetenas utförande. Hyresgästen ansvarar för att lägenheten är tömd inför renoveringsarbetet. Hyresvärden ansvarar inte för städning av lägenheten eller eventuellt kvarlämnat bohag i lägenheten.'),
            ], number ? { numbering: 'fixed', number } : {}),
        ];
    },
};
export const underskrift = legalSigningBlock(LEGAL_18, {
    blockKey: 'godkannande_18d.underskrift',
    title: 'Underskrift',
    summary: 'Place and date, then the signature panels of the tenant and an optional second tenant.',
    defaults: {
        ref: 'underskrift',
        title: 'Underskrift',
        parties: [
            { label: 'Hyresgäst 1', role: 'lessee', ordinal: 0 },
            { label: 'Eventuell hyresgäst 2', role: 'lessee', ordinal: 1 },
        ],
    },
    prelude: placeDateLine,
    slots: SIGNING_PLACE_DATE_SLOTS,
});
export const evakueringsavtal = {
    ...LEGAL_18,
    blockKey: 'godkannande_18d.evakueringsavtal',
    version: 'v1',
    title: 'Valfri bilaga: Evakueringsavtal',
    summary: 'Notes that a separate evacuation agreement accompanies the consent.',
    slots: [
        {
            key: 'annex_evakueringsavtal',
            label: 'Evakueringsavtal bifogas',
            type: 'boolean',
            help: 'Valfri bilaga — kryssa i om ett separat evakueringsavtal följer med.',
        },
    ],
    compile: (input, ctx) => shown(ctx, flag(input, 'annex_evakueringsavtal')) ? nodes(p([em('Valfri bilaga: Evakueringsavtal')], { variant: 'fine_print' })) : null,
};
export const GODKANNANDE_18D_BLOCKS = [
    hyresvard,
    hyresobjekt,
    hyresgast,
    byggbeskrivning,
    bilagor,
    overenskommelseRubrik,
    godkannande,
    underskrift,
    evakueringsavtal,
];
//# sourceMappingURL=blocks.js.map